import React from 'react'
import PropTypes from 'prop-types'
import { Motion, spring } from 'react-motion'
import cssModules from 'react-css-modules'
import styles from './campaign_info.less'
import { getI18n } from 'client/i18n'
import {
  formatMoney,
  formatDateShort,
  shouldUseIntlFormat,
} from 'client/griffin'
import RoundedProgress from 'components/common/rounded_progress'
import Separator from 'components/common/separator'
import Delay from 'components/common/delay'
import Money from 'components/common/money'
import { DAYS_THRESHOLD } from 'shared/shared_consts'

const PROGRESS_ANIMATION_DURATION = 250

const i18n = getI18n('components/chip_in')

class CampaignInfo extends React.Component {
  static propTypes = {
    currentValue: PropTypes.number,
    currency: PropTypes.string,
    daysPassed: PropTypes.number,
    daysRemaining: PropTypes.number,
    goal: PropTypes.number,
    contributionsCount: PropTypes.number,
    canChipIn: PropTypes.bool.isRequired,
    endDate: PropTypes.instanceOf(Date),
    preview: PropTypes.bool,
    shareProgress: PropTypes.bool,
    onClick: PropTypes.func,
    compact: PropTypes.bool,
    removeTrailingZeros: PropTypes.bool,
    title: PropTypes.string,
    pledge: PropTypes.number,
    name: PropTypes.string,
  }

  static defaultProps = {
    removeTrailingZeros: true,
  }

  getHistoryString = (daysPassed, contributionsCount) => {
    let historyKey
    if (daysPassed != null) {
      historyKey = 'age_only_{days}'
    }

    return historyKey
      ? i18n(historyKey, {
          days: daysPassed,
          contributions: contributionsCount,
        })
      : ''
  }

  render() {
    const { removeTrailingZeros, compact, pledge, name } = this.props
    const progressValue = Math.min(this.props.currentValue / this.props.goal, 1)
    const hasGoal = this.props.goal != null && this.props.goal > 0
    const sharingProgress = this.props.shareProgress || compact
    const hasProgress = sharingProgress && this.props.currentValue > 0
    const hasDaysPassed = this.props.daysPassed != null
    const hasContributionsCount = this.props.contributionsCount > 0
    const goalFormatted =
      hasGoal &&
      formatMoney(this.props.currency, this.props.goal, {
        removeTrailingZeros,
        hideCurrencyCode: true,
      })
    const historyString = this.getHistoryString(
      this.props.daysPassed,
      this.props.contributionsCount
    )
    const reached = progressValue >= 1
    const pledgeFormatted =
      pledge &&
      formatMoney(this.props.currency, pledge, { hideCurrencyCode: true })
    const showPledge = sharingProgress && pledge > 0

    let daysRemainingElement
    if (this.props.daysRemaining != null) {
      if (this.props.canChipIn) {
        daysRemainingElement = (
          <div styleName="remaining">
            <span styleName="calendar-icon" />
            {this.props.daysRemaining > DAYS_THRESHOLD
              ? i18n('ends_on', {
                  date: formatDateShort(this.props.endDate),
                })
              : i18n('remaining_{days}', {
                  days: this.props.daysRemaining + '',
                })}
          </div>
        )
      }
    }

    if (!sharingProgress && !hasGoal && !daysRemainingElement) {
      return null
    }
    return (
      <div>
        {compact &&
          !this.props.canChipIn && (
            <div>
              <div styleName="pool-ended">{i18n('ended')}</div>
              <Separator type="dotted" />
            </div>
          )}
        {compact && <div styleName="title">{this.props.title}</div>}
        {hasGoal &&
          reached &&
          compact && <div styleName="reached">{i18n('reached')}</div>}
        {sharingProgress ? (
          <div>
            {hasGoal ? (
              <Delay
                initial={0}
                value={progressValue}
                period={PROGRESS_ANIMATION_DURATION}
              >
                {delayed => (
                  <Motion
                    defaultStyle={{ x: 0 }}
                    style={{ x: spring(delayed) }}
                  >
                    {value => (
                      <RoundedProgress
                        value={value.x}
                        showProgress={hasGoal}
                        compact={compact}
                      />
                    )}
                  </Motion>
                )}
              </Delay>
            ) : (
              <RoundedProgress
                value={progressValue}
                showProgress={hasGoal}
                compact={compact}
              />
            )}
            <Money
              currency={this.props.currency}
              value={this.props.currentValue}
              removeTrailingZeros={removeTrailingZeros}
              showIntlCurrencyCode={false}
            />
            {hasGoal ? (
              <div styleName="goal" key="goal">
                {i18n('goal', { value: goalFormatted })}
              </div>
            ) : (
              <div styleName="collected">{i18n('collected')}</div>
            )}
          </div>
        ) : (
          hasGoal && (
            <div styleName="just-goal" key="goal">
              {i18n('just_goal', { value: goalFormatted })}
            </div>
          )
        )}
        {(hasGoal || hasProgress) &&
          shouldUseIntlFormat(this.props.currency) && (
            <div styleName="currency-code">{this.props.currency}</div>
          )}

        {!compact &&
          hasProgress &&
          (hasDaysPassed || hasContributionsCount) && (
            <div styleName="history" key="history">
              {historyString}
            </div>
          )}
        {showPledge && (
          <div styleName="pledged">
            {i18n('pledged' + (compact ? '' : '_name'), {
              amount: pledgeFormatted,
              name,
            })}
          </div>
        )}
        {((!compact && daysRemainingElement) ||
          (compact && showPledge && !hasContributionsCount)) && (
          <Separator type="dotted" />
        )}
        {!compact &&
          daysRemainingElement && <span>{daysRemainingElement}</span>}
      </div>
    )
  }
}

export default cssModules(CampaignInfo, styles, { allowMultiple: true })
